import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiZap, FiChevronRight } from 'react-icons/fi';
import { listActivity } from '../../api/activityApi';
import { isAiProposalsEnabled } from '../../helpers/agentFeature';

/* "AI worked for you today" — a dashboard strip that makes the intelligence
   layer's day visible at a glance. Reads the activity endpoint's `today`
   rollup { awaiting, confirmed, watched } (composed backend-side from the
   ledger). Pilot-gated; hides itself when there's nothing to show. */

const wrap = {
  display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap',
  padding: '12px 16px', margin: '0 0 14px',
  background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12,
};
const lead = { display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700, fontSize: 13, color: 'var(--text-main)' };
const spark = { width: 26, height: 26, borderRadius: 8, background: 'var(--accent-dim)', color: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 };
const statsRow = { display: 'flex', gap: 20, flexWrap: 'wrap', flex: 1 };
const stat = { display: 'flex', alignItems: 'baseline', gap: 6 };
const num = (accent) => ({ fontSize: 20, fontWeight: 800, color: accent ? 'var(--accent)' : 'var(--text-main)', lineHeight: 1 });
const lab = { fontSize: 12, color: 'var(--text-muted)' };
const review = { display: 'flex', alignItems: 'center', gap: 3, background: 'transparent', border: 'none', color: 'var(--accent)', fontWeight: 700, fontSize: 13, cursor: 'pointer', padding: 0, whiteSpace: 'nowrap' };

export default function AiWorkedStrip() {
  const enabled = isAiProposalsEnabled();
  const navigate = useNavigate();
  const [today, setToday] = useState(null);

  useEffect(() => {
    if (!enabled) return undefined;
    let alive = true;
    const fetch = () => listActivity({ limit: 1 })
      .then(({ data }) => { if (alive) setToday(data?.today || null); })
      .catch(() => { /* endpoint not up / transient — strip stays hidden */ });
    fetch();
    window.addEventListener('ai-proposals:changed', fetch);
    return () => { alive = false; window.removeEventListener('ai-proposals:changed', fetch); };
  }, [enabled]);

  if (!enabled || !today) return null;

  const stats = [
    { key: 'awaiting', n: today.awaiting || 0, label: 'awaiting your review', accent: true },
    { key: 'confirmed', n: today.confirmed || 0, label: 'confirmed today' },
    { key: 'watched', n: today.watched || 0, label: 'watched today' },
  ];
  // Quiet day: nothing acted on and nothing waiting — don't show a row of zeros.
  if (stats.every((s) => !s.n)) return null;

  return (
    <section style={wrap} aria-label="AI worked for you today">
      <span style={lead}>
        <span style={spark}><FiZap size={15} /></span>
        AI worked for you today
      </span>
      <div style={statsRow}>
        {stats.map((s) => (
          <span style={stat} key={s.key}>
            <span style={num(s.accent)}>{s.n}</span>
            <span style={lab}>{s.label}</span>
          </span>
        ))}
      </div>
      {today.awaiting > 0 && (
        <button style={review} onClick={() => navigate('/ai/proposals')}>
          Review <FiChevronRight size={14} />
        </button>
      )}
    </section>
  );
}
